import { useEffect, useState } from 'react';
import { locationApi } from '@/lib/api-paths';
import { useParams } from 'react-router-dom';
import { useTenantParams } from '@/hooks/use-tenant-params';
import { Toggle } from '@/components/inspector/toggle';
import { EmptyState } from '@/components/empty-state';
import { PageContainer } from '@/components/page-container';
import { PageError } from '@/components/page-error';
import { PageHeader } from '@/components/page-header';
import { Skeleton } from '@/components/skeleton';
import { invalidateMe, useMe } from '@/hooks/use-me';
import { api } from '@/lib/api';
import type { SiteDetailResponse, SiteMutationResponse } from '@/lib/types';

// =====================================================================
// GetFoundReady — WP1 Task 7 move. The "AI & indexing" card extracted
// VERBATIM from the old site-detail.tsx (llms.txt + AI-crawler-opt-out
// toggles, same handler, same copy) at its own route,
// locationRoutes.getFoundReady(businessId, siteId) ("/app/b/:siteId/foundation/get-found-ready").
//
// Plus a short, honest, display-only note about the two other things every
// pageboss site already does automatically — IndexNow pinging and
// structured-data (schema.org) markup — matching decision #18's "honest
// AEO marketing" rule: these are real, already-shipped, zero-marginal-cost
// levers (never gated, per CLAUDE.md), described here, not toggled. No new
// setting is introduced by this screen.
//
// The AEO audit card (findings → tasks) is NOT here — it moves to
// visibility/aeo.tsx in Task 8, per the brief.
// =====================================================================

export function GetFoundReady() {
  const meState = useMe();
  const { businessId = '', locationId: siteId } = useTenantParams();
  const [data, setData] = useState<SiteDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aiSettingsBusy, setAiSettingsBusy] = useState<'llmsTxt' | null>(null);
  const [aiSettingsError, setAiSettingsError] = useState<string | null>(null);

  useEffect(() => {
    // A Location read needs BOTH halves of the canonical route.
    // Missing either is "no Location in scope": request nothing.
    if (!businessId || !siteId) return;
    let cancelled = false;
    api
      .get<SiteDetailResponse>(locationApi(businessId, siteId, ''))
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, siteId]);

  // Quick-wins AEO (2026-08-07): saves the toggle, then re-syncs local state
  // from the SERVER's response — not an optimistic flip — so the switch
  // always reflects what actually landed in D1, and reverts itself on a
  // failed write instead of showing a state that was never persisted.
  // 2026-08-30 (operator): the "Ask AI assistants not to read this site"
  // switch was REMOVED, not disabled. AI reachability now keys on publication
  // status alone — a launched site is always readable by AI, an unlaunched one
  // never is — so the renderer stopped consulting the flag, and a switch the
  // product does not honour is a lie to the customer. Its write path is gone
  // from the API too (routes/api/sites.ts). llms.txt below is a SEPARATE
  // control and still works — it is now the ONLY setting this saves, so the
  // field parameter and the busy state are both single-valued rather than
  // unions with a dead second member.
  async function saveAiSetting(field: 'llmsTxtEnabled', next: boolean) {
    if (!siteId) return;
    setAiSettingsBusy('llmsTxt');
    setAiSettingsError(null);
    try {
      const result = await api.patch<SiteMutationResponse>(locationApi(businessId, siteId, ''), {
        [field]: next,
      });
      setData((prev) => (prev ? { ...prev, site: result.site } : prev));
    } catch (err) {
      setAiSettingsError(err instanceof Error ? err.message : String(err));
    } finally {
      setAiSettingsBusy(null);
    }
  }

  if (meState.kind === 'error') {
    // Keeps the page's own identity — same eyebrow and <h1> as the ready
    // state below — when /api/me fails. See components/page-error.tsx.
    return (
      <PageError
        eyebrow="Foundation"
        title="Get-found-ready settings"
        message={meState.message}
        retry={invalidateMe}
      />
    );
  }
  if (meState.kind !== 'ready') {
    return null;
  }

  if (error && !data) {
    return <EmptyState title="Couldn't load this site" description={error} />;
  }

  if (!data) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const { site } = data;

  return (
    <PageContainer>
      <PageHeader eyebrow="Foundation" title="Get-found-ready settings" />

      <section className="space-y-4 rounded-lg border border-border-subtle bg-card p-6">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">AI &amp; indexing</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Available on every plan.
          </p>
        </div>
        <div className="space-y-4">
          {/* One row — label+description left, switch right, vertically
              centred — at every width down to 375px (design audit A10):
              the switch never wraps below the label, it just holds its
              position on the row while the description text wraps under
              the label. gap-6 (wider than Toggle's own gap-3 default)
              keeps the switch from crowding a two-line description. */}
          <Toggle
            label="llms.txt (experimental)"
            description="No major AI platform has committed to reading this file."
            checked={site.llmsTxtEnabled}
            onChange={(next) => void saveAiSetting('llmsTxtEnabled', next)}
            disabled={aiSettingsBusy !== null}
            className="gap-6"
          />
        </div>
        {aiSettingsBusy ? (
          <p className="text-xs text-muted-foreground">Saving…</p>
        ) : null}
        {aiSettingsError ? (
          <p className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">
            {aiSettingsError}
          </p>
        ) : null}
      </section>

      <section className="space-y-3 rounded-lg border border-border-subtle bg-card p-6">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Already working for you</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            No setup needed — these run automatically on every plan.
          </p>
        </div>
        <ul className="space-y-2 text-sm text-text-secondary">
          <li>
            Every publish pings Bing&rsquo;s IndexNow so your pages get picked up faster —
            without waiting for a crawl.
          </li>
          <li>
            Every page carries structured data (schema.org business markup) so search engines
            can read your business details directly.
          </li>
        </ul>
      </section>
    </PageContainer>
  );
}
